import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    // La key de línea es estable aunque el carrito cambie por detrás (regalo
    // reclamado, app de bundles); el índice `line` queda solo para la UI.
    const key = event.target instanceof HTMLElement ? event.target.dataset.cartKey : undefined;

    // SIN key NO se muta: el fallback posicional {line} mutaba la línea
    // equivocada cuando el DOM estaba desincronizado (morphs fallidos en el
    // navegador in-app de IG/FB) — camisas convertidas en llaveros, guía
    // cobrada. Mejor perder un tap y re-sincronizar con el servidor.
    if (!key) {
      this.#hardResync();
      return;
    }

    // COHERENCIA cara-vs-matrícula (WebKit/iOS, 08/08): un morph aplicado a
    // medias puede dejar la fila con el CONTENIDO de un producto (título/foto,
    // que es lo que ve el cliente) pero la key de OTRO en los atributos del
    // input — los "+" del pantalón acababan en el llavero con key "correcta".
    // La key codifica el variant ("variantid:hash"); el enlace del título es
    // contenido y sobrevive al medio-morph: si no cuadran, la fila miente →
    // no mutar nada y repintar desde cero.
    const row = event.target instanceof HTMLElement ? event.target.closest('tr[data-key]') : null;
    const contentLink = row?.querySelector('a[href*="variant="]');
    if (contentLink) {
      const contentVariant = new URL(contentLink.getAttribute('href'), window.location.origin).searchParams.get('variant');
      const keyVariant = key.split(':')[0];
      if (contentVariant && keyVariant && contentVariant !== keyVariant) {
        this.#hardResync();
        return;
      }
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line, undefined, key);
    }

    this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#rowForLine(line, key);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Reconstruye ESTE componente desde el HTML fresco de la sección: busca su
   * gemelo en el HTML del servidor y vuelca su interior. Sin morphs — la
   * identidad de cada fila queda íntegra por construcción. El drawer/<dialog>
   * (ancestros) no se tocan, así que no se cierra ni pierde el scroll-lock.
   * @param {string} sectionHtml - HTML completo de la sección
   */
  #replaceFromSectionHtml(sectionHtml) {
    try {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const twins = doc.querySelectorAll('cart-items-component');
      let fresh = null;
      for (const t of twins) {
        if (t.getAttribute('data-section-id') === this.dataset.sectionId) fresh = t;
      }
      if (!fresh && twins.length === 1) fresh = twins[0];
      if (!fresh) return this.#hardResync();
      this.innerHTML = fresh.innerHTML;
      queueMicrotask(() => this.updatedCallback());
    } catch (e) {
      this.#hardResync();
    }
  }

  /**
   * Re-sincronización dura: pide la sección al servidor y reconstruye este
   * componente (ver #replaceFromSectionHtml). Último recurso: recargar.
   */
  #hardResync() {
    fetch(`${window.location.pathname}?section_id=${this.sectionId}`)
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const twins = doc.querySelectorAll('cart-items-component');
        let fresh = null;
        for (const t of twins) {
          if (t.getAttribute('data-section-id') === this.dataset.sectionId) fresh = t;
        }
        if (!fresh && twins.length === 1) fresh = twins[0];
        if (fresh) {
          this.innerHTML = fresh.innerHTML;
          queueMicrotask(() => this.updatedCallback());
        }
      })
      .catch(() => {
        window.location.reload();
      });
  }

  /**
   * Resuelve la fila de un ítem: por key si está disponible (estable frente a
   * carritos que cambiaron por detrás), con fallback al índice posicional.
   * @param {number} line - The line item index.
   * @param {string} [key] - The cart line item key.
   * @returns {HTMLTableRowElement | undefined}
   */
  #rowForLine(line, key) {
    if (key) {
      const row = this.refs.cartItemRows.find?.((row) => row.dataset.key === key);
      if (row) return row;
    }
    return this.refs.cartItemRows[line - 1];
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   * @param {Event} [event] - Click event from the remove button (on:click).
   * @param {string} [key] - The cart line item key, when already resolved.
   */
  onLineItemRemove(line, event, key) {
    let lineKey = key;
    if (!lineKey && event?.target instanceof Element) {
      lineKey = /** @type {HTMLElement | null} */ (event.target.closest('tr[data-key]'))?.dataset.key;
    }

    // Sin key no se borra nada por posición: re-sincronizar y salir (ver
    // #onQuantityChange). Borrar la línea 1 "a ciegas" era el mismo agujero.
    if (!lineKey) {
      this.#hardResync();
      return;
    }

    // Coherencia cara-vs-matrícula también al eliminar (ver #onQuantityChange).
    const removeRow = event?.target instanceof Element ? event.target.closest('tr[data-key]') : this.#rowForLine(line, lineKey);
    const removeLink = removeRow?.querySelector('a[href*="variant="]');
    if (removeLink) {
      const contentVariant = new URL(removeLink.getAttribute('href'), window.location.origin).searchParams.get('variant');
      if (contentVariant && contentVariant !== lineKey.split(':')[0]) {
        this.#hardResync();
        return;
      }
    }

    this.updateQuantity({
      line,
      key: lineKey,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#rowForLine(line, lineKey);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.key] - The cart line item key (preferred over line).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // `id` (key de línea) es inmune a que el carrito haya cambiado por detrás
    // (regalo, app de bundles). SIN fallback posicional: sin key no se llega
    // aquí (los callers re-sincronizan y salen).
    if (!key) {
      this.#hardResync();
      this.#enableCartItems();
      return;
    }
    const body = JSON.stringify({
      id: key,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        // Error del servidor O key obsoleta (la app de bundles reescribe
        // propiedades y la key cambia): antes esto se tragaba el error y el
        // drawer se quedaba enseñando un estado optimista FALSO (p. ej. "3
        // camisas" que el servidor nunca registró). Ahora: revertir el input
        // si se puede y re-sincronizar la sección con la verdad del servidor.
        if (parsedResponseText.errors || parsedResponseText.status || !parsedResponseText.sections?.[this.sectionId]) {
          try {
            if (parsedResponseText.errors) this.#handleCartError(line, parsedResponseText);
          } catch (e) {}
          // Repintado DURO, no morph: en WebKit los morphs pueden aplicarse a
          // medias y dejar el display mintiendo (el origen de todo esto).
          this.#hardResync();
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        // RECONSTRUCCION TOTAL (no morph): los morphs parciales en WebKit/iOS
        // dejaban filas con la cara de un producto y la identidad de otro
        // (atributos intercambiados en bloque burlaban incluso el chequeo de
        // coherencia). InnerHTML fresco del servidor = identidad siempre
        // integra. El <dialog> del drawer no se toca (es ancestro).
        this.#replaceFromSectionHtml(parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#replaceFromSectionHtml(cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      this.#hardResync();
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

